// Свой scroll anchoring для ленты с content-visibility: auto (этап 13.6).
//
// use:anchorHeight={{ shift }} на каждом .message-entry; один ResizeObserver на скроллер
// ([data-scroll-root]). Сообщение, целиком лежащее выше видимой области, при настоящем
// рендере меняет высоту (оценочные 160px → реальные, у длинного ответа тысячи пикселей),
// всё ниже сдвигается, и видимый контент уезжал бы. Суммируем такие дельты и отдаём в
// shift(delta): Chat.svelte прибавляет её к scrollTop до отрисовки — коллбэк ResizeObserver
// идёт после разметки и до paint, прыжка не видно. Браузерный anchoring (overflow-anchor)
// на скроллере выключен: WebKit его не умеет, а так Chromium и Safari ведут себя одинаково.
// Несколько сообщений могут вырасти в одном кадре: сортируем по положению сверху вниз и
// вычитаем из позиции каждого дельты всех выросших выше — так узнаём, где верх был до роста.
// Первое измерение узла (монтирование, подгрузка назад) сдвигом не считается: позицию при
// подгрузке восстанавливает сам loadMoreMessages.

const registry = new Map(); // root → { observer, nodes: Set, heights: Map<node, height>, shift }

function noop() {
  return { update() {}, destroy() {} };
}

function heightOf(entry) {
  const box = entry.borderBoxSize && entry.borderBoxSize[0];
  if (box && typeof box.blockSize === 'number') {
    return box.blockSize;
  }
  return entry.contentRect ? entry.contentRect.height : entry.target.getBoundingClientRect().height;
}

export function handleResizeEntries(root, state, entries) {
  const changes = [];
  for (const entry of entries) {
    const node = entry.target;
    const next = heightOf(entry);
    const prev = state.heights.get(node);
    state.heights.set(node, next);
    if (prev === undefined || next === prev) {
      continue;
    }
    const top = node.getBoundingClientRect().top - root.getBoundingClientRect().top;
    changes.push({ prev, delta: next - prev, top });
  }
  if (!changes.length) {
    return 0;
  }
  changes.sort((a, b) => a.top - b.top);
  let grownAbove = 0; // дельты всех выросших выше (для позиции «до роста»)
  let shift = 0; // дельты только тех, что целиком выше видимой области
  for (const change of changes) {
    const topBefore = change.top - grownAbove;
    if (topBefore + change.prev <= 0) {
      shift += change.delta;
    }
    grownAbove += change.delta;
  }
  return shift;
}

export function anchorHeight(node, options = {}) {
  if (typeof ResizeObserver === 'undefined' || typeof node.closest !== 'function') {
    return noop();
  }
  const root = node.closest('[data-scroll-root]');
  if (!root) {
    return noop();
  }

  let state = registry.get(root);
  if (!state) {
    state = { observer: null, nodes: new Set(), heights: new Map(), shift: () => {} };
    state.observer = new ResizeObserver((entries) => {
      const shift = handleResizeEntries(root, state, entries);
      if (shift) {
        state.shift(shift);
      }
    });
    registry.set(root, state);
  }
  if (typeof options.shift === 'function') {
    state.shift = options.shift;
  }
  state.nodes.add(node);
  state.observer.observe(node);

  return {
    update(next = {}) {
      if (typeof next.shift === 'function') {
        state.shift = next.shift;
      }
    },
    destroy() {
      state.observer.unobserve(node);
      state.nodes.delete(node);
      state.heights.delete(node);
      if (!state.nodes.size) {
        state.observer.disconnect();
        registry.delete(root);
      }
    },
  };
}
